<template>
	<div class="hidden lg:block">
		<!-- Debug indicators for trigger points with labels -->
		<div
			v-if="debug"
			ref="startTriggerRef"
			class="fixed left-0 w-full h-0.5 z-50 flex items-center pointer-events-none"
			:style="{ top: `${startTriggerPoint}px`, backgroundColor: 'rgba(0, 255, 0, 0.5)' }"
		>
			<span class="absolute left-2.5 bg-black/70 text-inverted text-xs py-0.5 px-1.5 rounded whitespace-nowrap">
				Start Trigger (default 60% viewport height)
			</span>
		</div>

		<div
			v-if="debug"
			ref="endTriggerRef"
			class="fixed left-0 w-full h-0.5 z-50 flex items-center pointer-events-none"
			:style="{ top: `${endTriggerPoint}px`, backgroundColor: 'rgba(255, 0, 0, 0.5)' }"
		>
			<span class="absolute left-2.5 bg-black/70 text-inverted text-xs py-0.5 px-1.5 rounded whitespace-nowrap">
				End Trigger (default 40% viewport height)
			</span>
		</div>

		<div
			v-if="debug"
			class="fixed flex gap-2 right-2.5 top-[146px] bg-black/70 text-inverted py-1 px-2.5 rounded z-50 text-xs w-[160px]"
		>
			Path Completion: <span
				:class="[
					'ml-auto',
					completionPercentage >= 99.9 ? 'text-red-500' : 'text-inverted'
				]"
			>
				{{ completionPercentage }}%
			</span>
		</div>

		<div
			v-if="debug"
			class="fixed flex gap-2 right-2.5 top-[116px] bg-black/70 text-inverted py-1 px-2.5 rounded z-50 text-xs"
		>
			Animation: <span :class="isAnimating ? 'text-green-500' : 'text-red-500'">
				{{ isAnimating ? 'Running' : 'Stopped' }}
			</span>
		</div>

		<div
			v-if="debug"
			class="fixed right-2.5 top-[70px] bg-black/70 text-inverted py-1 px-2.5 rounded z-50 text-xs"
		>
			Direction: <span>
				{{ scrollingDirection }}
			</span> | End Passed: <span :class="hasPassedEndTrigger ? 'text-red-500' : 'text-green-500'">
				{{ hasPassedEndTrigger ? 'Yes' : 'No' }}
			</span><br>
			Dynamic Trigger: <span>
				{{ dynamicStartTrigger !== null ? dynamicStartTrigger : '-' }}
			</span>
		</div>

		<!-- Path container for SVG paths and dots -->
		<div
			ref="pathContainerRef"
			:class="`absolute top-[100px] left-[calc(50%+220px)] -translate-x-1/2 w-[1302px] h-[${length}px] z-[-1] ${fiberClass}`"
		>
			<svg
				width="100%"
				height="100%"
				:viewBox="`0 0 1302 ${length}`"
				fill="none"
				preserveAspectRatio="xMidYMin meet"
				overflow="visible"
			>
				<!-- path1 -->
				<path
					ref="path1Ref"
					:d="path1d"
					stroke="url(#A)"
					stroke-width="4"
				/>
				<!-- path2 -->
				<path
					ref="path2Ref"
					:d="path2d"
					stroke="url(#B)"
					stroke-width="2"
				/>

				<!-- dots -->
				<g
					ref="dot1Ref"
					class="transition-opacity duration-200 ease-in-out"
					:class="dotsVisible ? 'opacity-100' : 'opacity-0'"
				>
					<circle
						:cx="dot1Position.x"
						:cy="dot1Position.y"
						r="5"
						fill="#FF5733"
						filter="url(#glow-filter)"
					/>
				</g>
				<g
					ref="dot2Ref"
					class="transition-opacity duration-200 ease-in-out"
					:class="dotsVisible ? 'opacity-100' : 'opacity-0'"
				>
					<circle
						:cx="dot2Position.x"
						:cy="dot2Position.y"
						r="5"
						fill="#FF5733"
						filter="url(#glow-filter)"
					/>
				</g>

				<!-- filter -->
				<defs>
					<filter
						id="glow-filter"
						x="-300%"
						y="-300%"
						width="600%"
						height="600%"
						filterUnits="objectBoundingBox"
						color-interpolation-filters="sRGB"
					>
						<feFlood
							flood-opacity="0"
							result="BackgroundImageFix"
						/>
						<feColorMatrix
							in="SourceAlpha"
							type="matrix"
							values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							result="hardAlpha"
						/>
						<feOffset />
						<feGaussianBlur stdDeviation="5" />
						<feComposite
							in2="hardAlpha"
							operator="out"
						/>
						<feColorMatrix
							type="matrix"
							values="0 0 0 0 1 0 0 0 0 0.341176 0 0 0 0 0.2 0 0 0 1 0"
						/>
						<feBlend
							mode="normal"
							in2="BackgroundImageFix"
							result="effect1_dropShadow"
						/>
						<feColorMatrix
							in="SourceAlpha"
							type="matrix"
							values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							result="hardAlpha"
						/>
						<feMorphology
							radius="2"
							operator="dilate"
							in="SourceAlpha"
							result="effect2_dropShadow"
						/>
						<feOffset />
						<feComposite
							in2="hardAlpha"
							operator="out"
						/>
						<feColorMatrix
							type="matrix"
							values="0 0 0 0 1 0 0 0 0 0.341176 0 0 0 0 0.2 0 0 0 0.25 0"
						/>
						<feBlend
							mode="normal"
							in2="effect1_dropShadow"
							result="effect2_dropShadow"
						/>
						<feBlend
							mode="normal"
							in="SourceGraphic"
							in2="effect2_dropShadow"
							result="shape"
						/>
					</filter>
					<linearGradient
						id="B"
						x1="1294"
						y1="-36.643"
						x2="233.162"
						y2="2609.65"
						gradientUnits="userSpaceOnUse"
					>
						<stop
							stop-color="#ff5733"
							stop-opacity=".05"
						/>
						<stop
							offset=".37"
							stop-color="#ff5733"
						/>
						<stop
							offset="1"
							stop-color="#ee8b2c"
						/>
					</linearGradient>
					<linearGradient
						id="A"
						x1="1281"
						y1="-48.643"
						x2="220.161"
						y2="2597.65"
						gradientUnits="userSpaceOnUse"
					>
						<stop
							stop-color="#ff5733"
							stop-opacity=".05"
						/>
						<stop
							offset=".37"
							stop-color="#ff5733"
						/>
						<stop
							offset="1"
							stop-color="#ee8b2c"
						/>
					</linearGradient>
				</defs>
			</svg>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useFiberAnimation } from '@/composables/useFiberAnimation'

const props = defineProps({
	debug: {
		type: Boolean,
		default: false
	},
	startTriggerViewportPosition: {
		type: Number,
		default: 0.6
	},
	endTriggerViewportPosition: {
		type: Number,
		default: 0.4
	},
	length: {
		type: Number,
		default: 2776
	},
	fiberClass: {
		type: String,
		default: ''
	}
})
const {
	pathContainerRef,
	path1Ref,
	path2Ref,
	dot1Ref,
	dot2Ref,
	dot1Position,
	dot2Position,
	dotsVisible,
	completionPercentage,
	scrollingDirection,
	isAnimating,
	hasPassedEndTrigger,
	dynamicStartTrigger,
	startTriggerPoint,
	endTriggerPoint
} = useFiberAnimation(
	props.startTriggerViewportPosition,
	props.endTriggerViewportPosition
)

const { length } = toRefs(props)

const path1d = computed(() => `M1302 1H902C860.579 1 827 34.5786 827 76V395C827 436.421 793.421 470 752 470H76C34.5786 470 1 503.579 1 545V${length.value}`)
const path2d = computed(() => `M1302 13H915C873.579 13 840 46.5786 840 88V407C840 448.421 806.421 482 765 482H89C47.5786 482 14 515.579 14 557V${length.value}`)
</script>
